import { create, test, enforce, only } from "vest";
import type { Suite, SuiteResult } from "vest";
import type { CB } from "vest-utils";
import { readable, writable, get, derived } from "svelte/store";
import type { Readable, Writable, Subscriber, Unsubscriber, Updater } from "svelte/store";
import { JsonPointer } from "json-ptr";
import { walk, reduce, deepCopy } from "walkjs";
import type { ProblemDocument } from "http-problem-details";

type OmitFirst<T extends unknown[]> = T extends [unknown, ...infer R] ? R : never
type ValidatingStoreValueType = "primitive" | "array" | "nested";

export type StoreStateItem<T> = T extends Array<infer U> ?
	ValidatingStoreArray<U> & Array<StoreStateItem<U>> :
	T extends string | boolean | number ?
	ValidatingStoreValue<T> :
	ValidatingStoreNested<T> & StoreState<T>

export type StoreState<T> = { [P in keyof T]-?: StoreStateItem<T[P]> }

export interface ValidationProblemDetails extends ProblemDocument {
	errors: Record<string, string[]>;
}

export class ValidatingStoreValue<T = any> {
	protected _valueStore: Writable<any>;
	protected _isValid: boolean = false;
	protected _hasErrors: boolean = false;
	protected _errors: string[] = [];
	protected _externalErrors: string[] = [];
	protected _value: T;

	readonly pointer: JsonPointer;
	readonly path: string;
	isDirty: boolean = false;

	get $type(): ValidatingStoreValueType {
		return "primitive";
	}

	get isValid(): boolean {
		return this._isValid;
	}

	get errors(): string[] {
		if (!this.isDirty) {
			return this._externalErrors;
		}
		return this._errors.concat(this._externalErrors);
	}

	get hasErrors(): boolean {
		return (this.isDirty && this._hasErrors) || this._externalErrors.length > 0;
	}

	get value(): T {
		return this._value;
	}
	set value(value: T) {
		this._valueStore.update(state => {
			this.pointer.set(state, value);
			return state;
		});
	}

	constructor(pointer: JsonPointer, validationStore: Readable<SuiteResult<string, string>>, externalErrorsStore: Readable<Record<string, string[]>>, valueStore: Writable<any>) {
		this.pointer = pointer;
		this.path = pointer.path.join(".");
		this._valueStore = valueStore;

		validationStore.subscribe(state => {
			this._isValid = state.isValid(this.path);
			this._hasErrors = state.hasErrors(this.path);
			this._errors = state.getErrors(this.path);
			this._externalErrors = [];
		});
		externalErrorsStore.subscribe(state => {
			this._externalErrors = state[this.path] ?? [];
		});
		valueStore.subscribe(state => {
			this._value = this.pointer.get(state) as T;
		});
	}

	touch() {
		if (this.value != null && this.value != "") {
			this.isDirty = true;
		}
	}
}

export class ValidatingStoreArray<T> extends ValidatingStoreValue<T[]> {
	private _items: StoreStateItem<T>[];

	get $type(): ValidatingStoreValueType {
		return "array";
	}

	constructor(pointer: JsonPointer, validationStore: Readable<SuiteResult<string, string>>, externalErrorsStore: Readable<Record<string, string[]>>, valueStore: Writable<any>, items: StoreStateItem<T>[]) {
		super(pointer, validationStore, externalErrorsStore, valueStore);
		this._items = items;

		return new Proxy(this, {
			get(target, prop, receiver) {
				if (prop in target._items) {
					let value = target._items[prop as any] as any;

					// wrap array mutating functions to trigger an update of the store
					if (typeof value === 'function') {
						return function (...args: any[]) {
							let retVal = value.apply(target._items, args);
							switch (prop) {
								case "fill":
								case "reverse":
								case "sort":
								case "push":
								case "pop":
								case "splice":
								case "slice":
								case "shift":
								case "unshift":
									target._valueStore.update(state => {
										return state;
									});
							}
							return retVal;
						};
					}

					return value;
				}
				return Reflect.get(target, prop, receiver);
			}
		});
	}

	touch() {
		this.isDirty = true;
	}
}

export class ValidatingStoreNested<T = object> extends ValidatingStoreValue<T> {
	private _props: any;

	get $type(): ValidatingStoreValueType {
		return "nested";
	}

	constructor(pointer: JsonPointer, validationStore: Readable<SuiteResult<string, string>>, externalErrorsStore: Readable<Record<string, string[]>>, valueStore: Writable<any>, props: StoreState<T>) {
		super(pointer, validationStore, externalErrorsStore, valueStore);
		this._props = props;

		return new Proxy(this, {
			get(target, prop, receiver) {
				if (typeof prop === "string" && target._props.hasOwnProperty(prop)) {
					return target._props[prop];
				}
				return Reflect.get(target, prop, receiver);
			},
			ownKeys(target) {
				return [...Object.keys(target._props), ...Reflect.ownKeys(target)];
			},
			getOwnPropertyDescriptor(target, prop) {
				if (typeof prop === "string" && target._props.hasOwnProperty(prop)) {
					return {
						enumerable: true,
						configurable: true
					};
				}
				return Reflect.getOwnPropertyDescriptor(target, prop);
			}
		});
	}
}

export class ValidatingStore<TState extends object, TValidationFunc extends CB> implements Writable<StoreState<TState>> {
	#initialData: TState;
	#vmStore: Writable<StoreState<TState>>;
	#externalErrorsStore: Writable<Record<string, string[]>>;
	#aggregateStore: Readable<StoreState<TState>>;

	readonly valueStore: Writable<TState>;
	readonly validationStore: Writable<SuiteResult<keyof TState & string, string>>;
	readonly suite: Suite<keyof TState & string, string, CB>;

	constructor(initialData: TState, suiteCallback: TValidationFunc) {
		this.#initialData = deepCopy(initialData) as TState;
		this.#externalErrorsStore = writable({});

		this.valueStore = writable(deepCopy(initialData) as TState);

		this.suite = create(suiteCallback) as any;
		this.validationStore = writable(this.suite.get());
		this.suite.subscribe(() => {
			this.validationStore.update(_ => this.suite.get());
		});

		this.valueStore.subscribe(state => {
			this.validationStore.set(this.suite(state));
		});

		this.#vmStore = writable(this.#createStoreState(initialData));

		this.#aggregateStore = derived([this.valueStore, this.#vmStore, this.#externalErrorsStore], values => values[1]);
	}

	subscribe(run: Subscriber<StoreState<TState>>): Unsubscriber {
		return this.#aggregateStore.subscribe(run);
	}

	validate(...args: OmitFirst<Parameters<TValidationFunc>>): boolean {
		this.update(state => {
			for (let storeItem of Object.values(state)) {
				this.#recursivelySetDirty(storeItem);
			}
			return state;
		});

		this.validationStore.set(this.suite(this.getData(), ...args));
		return get(this.validationStore).isValid();
	}

	validateGroup(groupName: string, ...args: OmitFirst<Parameters<TValidationFunc>>): boolean {
		let validationResult = get(this.validationStore);
		let fieldNamesInGroup = Object.keys(validationResult.groups[groupName]);
		this.update(state => {
			for (let storeItem of Object.values(state)) {
				this.#recursivelySetDirty(storeItem, fieldNamesInGroup);
			}
			return state;
		});

		this.validationStore.set(this.suite(this.getData(), ...args));
		return get(this.validationStore).isValidByGroup(groupName);
	}

	setInitialData(data: TState) {
		this.#initialData = deepCopy(data) as TState;
	}

	clear() {
		this.valueStore.set(deepCopy(this.#initialData) as TState);
		this.set(this.#createStoreState(this.#initialData));
		this.suite.reset();
		this.validationStore.set(this.suite(this.getData()));
	}

	getData(): TState {
		return get(this.valueStore);
	}

	getValidationResult(): SuiteResult<keyof TState & string, string> {
		return get(this.validationStore);
	}

	updateData(updater: Updater<TState>): void {
		this.valueStore.update(state => {
			let newState = updater(state);
			return deepCopy(newState) as TState;
		});
		this.set(this.#createStoreState(get(this.valueStore)));
	}

	set(value: StoreState<TState>): void {
		this.#vmStore.set(value);
		this.validationStore.set(this.suite(this.getData()));
	}

	update(updater: Updater<StoreState<TState>>): void {
		this.#vmStore.update(updater);
		this.validationStore.set(this.suite(this.getData()));
	}

	setExternalErrors(problemDetails: ValidationProblemDetails) {
		let errors: Record<string, string[]> = {};
		for (let [field, externalErrors] of Object.entries(problemDetails.errors)) {
			let camelCasedField = field.split(".").map(x => x.substring(0, 1).toLowerCase() + x.substring(1)).join(".");
			let path = camelCasedField.replace(/\[(\d+)\]\./, ".$1.");
			errors[path] = externalErrors;
		}
		this.#externalErrorsStore.set(errors);
	}
	clearExternalErrors() {
		this.#externalErrorsStore.set({});
	}

	#createStoreState(data: any): StoreState<TState> {
		let items: any = {};
		for (let [propName, value] of Object.entries(data)) {
			items[propName] = createRecursive(`/${propName}`, value, this.valueStore, this.validationStore, this.#externalErrorsStore);
		}
		return items;

		function createRecursive(pointer: string, value: any, valueStore: any, validationStore: Readable<SuiteResult<string, string>>, externalErrorsStore: Writable<Record<string, string[]>>): any {
			var jsonPointer = new JsonPointer(pointer);

			if (Array.isArray(value)) {
				let items = value.map((x, i) => createRecursive(`${pointer}/${i}`, x, valueStore, validationStore, externalErrorsStore));
				return new ValidatingStoreArray(jsonPointer, validationStore, externalErrorsStore, valueStore, items);

			} else if (value != null && typeof value === "object" && Object.keys(value).length > 0) {
				let nested: any = {};
				for (let [subProp, subPropValue] of Object.entries(value)) {
					nested[subProp] = createRecursive(`${pointer}/${subProp}`, subPropValue, valueStore, validationStore, externalErrorsStore);
				}
				return new ValidatingStoreNested(jsonPointer, validationStore, externalErrorsStore, valueStore, nested);

			} else {
				return new ValidatingStoreValue(jsonPointer, validationStore, externalErrorsStore, valueStore);
			}
		}
	}

	#recursivelySetDirty(storeItem: any, fieldNamesInGroup?: string[]) {
		if (storeItem instanceof ValidatingStoreArray) {
			for (let item of storeItem as unknown as any[]) {
				this.#recursivelySetDirty(item, fieldNamesInGroup);
			}
		}
		else if (storeItem instanceof ValidatingStoreNested) {
			for (let prop of Object.values(storeItem)) {
				this.#recursivelySetDirty(prop, fieldNamesInGroup);
			}
		}
		else if (storeItem instanceof ValidatingStoreValue) {
			if (!fieldNamesInGroup || fieldNamesInGroup.includes(storeItem.path)) {
				storeItem.isDirty = true;
			}
		}
	}
}
